// Archive / Restore / batch pre-confirm dialogs for the catalog reference
// entities. The shell below is the Song Library's confirmation layout
// (src/pages/songs.tsx) factored out verbatim — width 430, centred, 32px pad,
// 17px semibold title, #666 body, Cancel + primary action — so Record Labels,
// Artists, and Albums present the same dialog the operator already knows.

import React from 'react';
import { Modal, Button as AntdButton } from 'antd';
import type { CatalogEntityKind } from './catalogLifecycleTypes';
import {
  archiveConsequence,
  batchArchiveConsequence,
  batchRestoreConsequence,
  catalogCopy,
  restoreConsequence,
} from './catalogEntityCopy';
import styles from './CatalogConfirmModals.module.scss';

interface CenteredConfirmModalProps {
  open: boolean;
  title: React.ReactNode;
  body: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  testId?: string;
}

function CenteredConfirmModal({
  open,
  title,
  body,
  confirmLabel,
  danger,
  loading,
  onCancel,
  onConfirm,
  testId,
}: CenteredConfirmModalProps) {
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width={430}
      className="kk-modal-center-in-content"
    >
      <div style={{ textAlign: 'center', padding: 32 }} data-testid={testId}>
        <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>{title}</div>
        <div style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>{body}</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
          <AntdButton onClick={onCancel} disabled={loading}>
            Cancel
          </AntdButton>
          <AntdButton
            type="primary"
            danger={danger}
            className={danger ? undefined : styles.brandPrimary}
            loading={loading}
            onClick={onConfirm}
          >
            {confirmLabel}
          </AntdButton>
        </div>
      </div>
    </Modal>
  );
}

interface SingleTargetModalProps {
  kind: CatalogEntityKind;
  open: boolean;
  name: string | undefined;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function CatalogArchiveModal({
  kind,
  open,
  name,
  loading,
  onCancel,
  onConfirm,
}: SingleTargetModalProps) {
  return (
    <CenteredConfirmModal
      open={open}
      testId={`${kind}-archive-modal`}
      title={<>Archive &quot;{name}&quot;?</>}
      body={archiveConsequence(kind)}
      confirmLabel="Archive"
      danger
      loading={loading}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

export function CatalogRestoreModal({
  kind,
  open,
  name,
  loading,
  onCancel,
  onConfirm,
}: SingleTargetModalProps) {
  return (
    <CenteredConfirmModal
      open={open}
      testId={`${kind}-restore-modal`}
      title={<>Restore &quot;{name}&quot;?</>}
      body={restoreConsequence(kind)}
      confirmLabel="Restore"
      loading={loading}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

interface CatalogBatchConfirmModalProps {
  kind: CatalogEntityKind;
  open: boolean;
  /** 'archive' | 'restore' — permanent delete never uses this dialog. */
  action: 'archive' | 'restore' | null;
  selectedCount: number;
  /**
   * True when the action runs against the status-scoped subset of a mixed
   * All-tab selection — the title then names the scope (Active/Archived).
   */
  mixedScoped?: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Batch pre-confirm for archive/restore only. Bulk permanent delete skips
 * this dialog entirely and opens the preflight modal instead, so there is
 * exactly one permanent-delete confirmation experience (Song Library rule).
 */
export function CatalogBatchConfirmModal({
  kind,
  open,
  action,
  selectedCount,
  mixedScoped,
  loading,
  onCancel,
  onConfirm,
}: CatalogBatchConfirmModalProps) {
  const { noun } = catalogCopy(kind);
  const isRestore = action === 'restore';
  const scope = mixedScoped ? (isRestore ? 'Archived ' : 'Active ') : '';
  return (
    <CenteredConfirmModal
      open={open}
      testId={`${kind}-batch-modal`}
      title={
        isRestore
          ? `Restore ${selectedCount} selected ${scope}${noun}(s)?`
          : `Archive ${selectedCount} selected ${scope}${noun}(s)?`
      }
      body={isRestore ? batchRestoreConsequence(kind) : batchArchiveConsequence(kind)}
      confirmLabel={isRestore ? 'Restore' : 'Archive'}
      danger={!isRestore}
      loading={loading}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}
